import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, BookOpen } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, MaskedLines } from "../components/Motion";
import Seo from "../components/Seo";
import Newsletter from "../components/Newsletter";
import { ZR_SERIES, ZR_BANDS, ZR_UI } from "../i18n/zwischenraum";

const ZwischenraumSeries = () => {
  const { lang } = useLang();
  const ui = ZR_UI[lang] || ZR_UI.de;

  const seriesSchema = {
    "@context": "https://schema.org",
    "@type": "Book",
    name: ZR_SERIES.title,
    bookFormat: "https://schema.org/EBook",
    inLanguage: "de",
    numberOfPages: ZR_BANDS.length,
    author: { "@type": "Organization", name: "MYSYMP AG" },
    description: ZR_SERIES.intro,
    hasPart: ZR_BANDS.map((b) => ({ "@type": "Book", name: b.title, url: `https://mysymp.ch/library/zwischenraum/${b.slug}` })),
  };

  return (
    <div data-testid="zwischenraum-series" className="bg-obsidian">
      <Seo pageKey="library" path="/library/zwischenraum" title={ZR_SERIES.title} description={ZR_SERIES.claim} schema={[seriesSchema]} />

      {/* Hero */}
      <section className="mx-auto max-w-[1400px] px-6 pt-40 pb-20 md:px-12 md:pt-52">
        <Link to="/library" className="mb-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/45 transition-colors hover:text-champagne">
          <ArrowLeft size={14} /> MYSYMP Library
        </Link>
        <span className="block text-xs uppercase tracking-[0.3em] text-champagne">{ui.overline}</span>
        <h1 className="mt-6 max-w-4xl font-serif text-5xl font-light leading-[0.98] tracking-tight text-white md:text-7xl">
          <MaskedLines lines={["Die Zwischenraum-", "Geschichten"]} startDelay={0.2} />
        </h1>
        <Reveal delay={0.4}>
          <p className="mt-8 max-w-2xl font-serif text-xl font-light italic leading-relaxed text-white/80 md:text-2xl">{ZR_SERIES.claim}</p>
        </Reveal>
        <Reveal delay={0.5}>
          <p className="mt-6 max-w-xl text-sm font-light leading-relaxed text-white/50">{ZR_SERIES.slogan}</p>
          <a href="#baende" data-testid="zr-hero-cta" className="mt-10 inline-flex items-center gap-3 bg-champagne px-8 py-4 text-xs uppercase tracking-[0.2em] text-obsidian transition-colors hover:bg-champagne-light">
            {ui.discover} <ArrowRight size={15} />
          </a>
        </Reveal>
      </section>

      {/* Intro + movements */}
      <section className="border-t border-white/10 bg-obsidian-soft py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-6 md:grid-cols-12 md:px-12">
          <div className="md:col-span-7">
            <Reveal><p className="font-serif text-2xl font-light leading-relaxed text-white md:text-3xl">{ZR_SERIES.intro}</p></Reveal>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <Reveal delay={0.1}>
              <ul className="space-y-6">
                {ZR_SERIES.movements.map((m, i) => (
                  <li key={i} className="border-l-2 border-champagne pl-5">
                    <p className="font-serif text-lg font-light text-white">{m.t}</p>
                    <p className="mt-1 text-sm font-light text-white/50">{m.d}</p>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Three entries */}
      <section className="border-t border-white/10 bg-obsidian py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal><span className="text-xs uppercase tracking-[0.3em] text-champagne">{ui.entriesTitle}</span></Reveal>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {ZR_SERIES.entries.map((e, i) => (
              <Reveal key={e.key} delay={i * 0.1}>
                <div className="h-full border border-white/10 bg-obsidian-soft p-8">
                  <h3 className="font-serif text-xl font-light text-white">{e.head}</h3>
                  <p className="mt-5 border-l-2 border-champagne/50 pl-4 font-serif text-lg font-light italic leading-relaxed text-white/70">„{e.line}“</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 12 volumes catalog */}
      <section id="baende" className="border-t border-white/10 bg-obsidian-soft py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 className="font-serif text-4xl font-light text-white md:text-5xl">{ui.volumes}</h2>
          </Reveal>
          <div className="mt-14 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3" data-testid="zr-catalog">
            {ZR_BANDS.map((b, i) => (
              <Reveal key={b.slug} delay={(i % 3) * 0.08}>
                <Link to={`/library/zwischenraum/${b.slug}`} data-testid={`zr-band-${b.slug}`} className="group block">
                  <div className="relative aspect-[3/4] overflow-hidden border border-white/10 bg-obsidian">
                    <img src={b.cover} alt={b.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105" />
                    <span className="absolute left-4 top-4 flex h-8 w-8 items-center justify-center rounded-full border border-champagne/50 bg-obsidian/60 text-xs text-champagne backdrop-blur">{b.n}</span>
                  </div>
                  <h3 className="mt-5 font-serif text-xl font-light leading-snug text-white transition-colors group-hover:text-champagne">{b.title}</h3>
                  <p className="mt-2 text-xs font-light uppercase tracking-wider text-white/40">{b.subtitle}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Building blocks */}
      <section className="border-t border-white/10 bg-obsidian py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal><span className="text-xs uppercase tracking-[0.3em] text-champagne">{ui.blocks}</span></Reveal>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {ZR_SERIES.building_blocks.map((b, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="h-full border-t border-white/15 pt-5">
                  <h3 className="font-serif text-lg font-light text-champagne">{b.t}</h3>
                  <p className="mt-3 text-sm font-light leading-relaxed text-white/55">{b.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <div className="border-t border-white/10 bg-obsidian-soft py-6">
        <Reveal><p className="mx-auto max-w-2xl px-6 text-center font-serif text-lg font-light italic text-white/50">{ui.newsletterTitle} — {ui.newsletterSub}</p></Reveal>
      </div>
      <Newsletter />
    </div>
  );
};

export default ZwischenraumSeries;
